// Inspecteur → Bestandssystemen (filing-systems register) — FRONTEND-BLUEPRINT §3.1/§4 + PLAN-W27 B.
// Same RegisterScreen kit as inspector/assets.tsx: PageHeader + search + DataTable + create/edit
// Dialog + deactivate, over api.listBestandssystemen()/createBestandssysteem() (RLS-scoped).
// DEFERRED-A3: the legacy detail page was never captured, so the field set stays minimal +
// grounded (name · category · description). The M:N link to Verwerkingen is edited on the
// Verwerking detail (its Bestandssystemen junction panel).
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { TENANT_PATHS } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { Can } from "../../lib/rbac";
import { useI18n } from "../../lib/i18n";
import type { Column } from "@/components/data-table";
import {
  RegisterScreen, CreateDialog, EditDialog, DeactivateButton, TagBadge, truncate, type Row, type FormField,
} from "./_shared";

const FIELDS: FormField[] = [
  { key: "name", label: "Naam", required: true },
  { key: "category", label: "Categorie" },
  { key: "description", label: "Omschrijving", type: "textarea" },
];

export function InspectorFilingSystemsRoute() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["bestandssystemen"] });
  const [editing, setEditing] = useState<string | null>(null);

  const columns: Column<Row>[] = [
    { key: "category", header: "Categorie", render: (r) => <TagBadge value={r.category} /> },
    { key: "name", header: "Naam", render: (r) => <span className="font-medium">{String(r.name ?? "—")}</span> },
    { key: "description", header: "Omschrijving", render: (r) => truncate(r.description) },
  ];

  return (
    <>
      <RegisterScreen
        title={t("nav.filingSystems")}
        description={t("inspector.filing.sub")}
        help={t("help.filingSystems")}
        queryKey="bestandssystemen"
        listFn={() => api.listBestandssystemen()}
        columns={columns}
        empty={t("inspector.filing.empty")}
        readPerm="bestandssysteem.read"
        onRowClick={(r) => { if (r.id) void navigate({ to: "/inspector/filing-systems/$id", params: { id: String(r.id) } }); }}
        actions={
          <Can perm="bestandssysteem.write">
            <CreateDialog
              title={t("inspector.filing.new")}
              fields={FIELDS}
              onCreate={(i) => api.createBestandssysteem(i as unknown as Parameters<typeof api.createBestandssysteem>[0])}
              onDone={invalidate}
            />
          </Can>
        }
        rowActions={(r) => {
          const id = String(r.id);
          return (
            <span className="inline-flex items-center gap-1">
              <Can perm="bestandssysteem.update"><button type="button" className="text-xs text-primary hover:underline" onClick={() => setEditing(id)}>{t("crud.edit")}</button></Can>
              <Can perm="bestandssysteem.delete"><DeactivateButton basePath={TENANT_PATHS.bestandssystemen} id={id} onDone={invalidate} /></Can>
            </span>
          );
        }}
      />
      {editing && (
        <EditDialog
          basePath={TENANT_PATHS.bestandssystemen}
          id={editing}
          title={t("crud.edit")}
          fields={FIELDS}
          onClose={() => setEditing(null)}
          onDone={() => { invalidate(); setEditing(null); }}
        />
      )}
    </>
  );
}
